'use client';

import { ChangeEvent, useCallback, useState } from 'react';
import { autoComplete } from '@/types/html';
import cn from 'classnames';
import { HTMLGoogleAnalyticsData } from '@/types/globals';
import {
	characterCountLimitClassNames,
	contextRowClassNames,
	fieldSetClassNames,
	helperTextClassNames,
	inputClassNames,
	labelClassNames,
} from '../Inputs/styles';
import styles from './textarea.module.css';

export interface TextareaProps extends HTMLGoogleAnalyticsData {
	testId?: string;
	contextRowClassName?: string;
	contextRowProps?: Omit<React.HTMLAttributes<HTMLDivElement>, 'className'>;
	fieldsetClassName?: string;
	fieldsetProps?: Omit<React.HTMLAttributes<HTMLFieldSetElement>, 'className'>;
	inputClassName?: string;
	inputProps?: React.HTMLAttributes<HTMLTextAreaElement>;
	labelClassName?: string;
	labelProps?: Omit<
		React.HTMLAttributes<HTMLLabelElement>,
		'htmlFor' | 'className'
	>;
	helperText?: string;
	label: string;
	onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
	onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
	autocomplete?: autoComplete;
	cols?: number;
	disabled?: boolean;
	id: string;
	maxlength?: number;
	minlength?: number;
	name: string;
	required?: boolean;
	rows?: number;
	spellcheck?: boolean;
	value?: string;
}

const Textarea = (props: TextareaProps) => {
	const {
		testId = 'Textarea',
		contextRowClassName,
		contextRowProps,
		fieldsetClassName,
		fieldsetProps,
		inputClassName,
		inputProps,
		labelClassName,
		labelProps,
		helperText = '',
		label,
		onBlur,
		onChange,
		autocomplete = 'off',
		cols,
		disabled,
		id,
		maxlength,
		minlength,
		name,
		required,
		rows,
		spellcheck,
		value,
		...gaData
	} = props;
	const [characterCount, setCharacterCount] = useState(0);

	const manInTheMiddleOnChange = useCallback(
		(e: ChangeEvent<HTMLTextAreaElement>) => {
			setCharacterCount(e.target.value.length);

			if (onChange) {
				onChange(e);
			}
		},
		[onChange],
	);

	return (
		<fieldset
			{...fieldsetProps}
			className={cn(fieldsetClassName, fieldSetClassNames)}
			data-testid={testId + '-FieldSet'}
			{...gaData}
		>
			<div
				role="presentation"
				className="absolute left-px top-px block w-[99%] rounded-2xl bg-white pb-4 pt-3"
			/>
			<textarea
				{...inputProps}
				autoComplete={autocomplete}
				disabled={disabled}
				className={cn(
					inputClassName,
					inputClassNames,
					styles['hide-scroll'],
					'resize-none',
				)}
				cols={cols}
				id={id}
				maxLength={maxlength}
				minLength={minlength}
				placeholder=" "
				name={name}
				onBlur={onBlur}
				onChange={manInTheMiddleOnChange}
				required={required}
				rows={rows}
				spellCheck={spellcheck}
				value={value}
				data-testid={testId}
			/>
			<label
				{...labelProps}
				htmlFor={id}
				className={cn(labelClassName, labelClassNames)}
			>
				{label}
			</label>
			<div
				{...contextRowProps}
				className={cn(contextRowClassName, contextRowClassNames)}
			>
				<p
					className={cn(helperTextClassNames, {
						hidden: !helperText,
					})}
				>
					{helperText}
				</p>
				<p
					className={cn(characterCountLimitClassNames, {
						'ml-auto': !helperText,
						hidden: !maxlength,
					})}
				>
					{characterCount}/{maxlength}
				</p>
			</div>
		</fieldset>
	);
};

export default Textarea;
